/**
 * Every call is same-origin, so the session cookie rides along automatically.
 * Nothing here sends identity — the server reads it from the cookie, and the
 * client is never trusted to say who it is.
 */
export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Content-Type only when there is something to type. Declaring
  // application/json on a bodyless POST makes Fastify reject the request with
  // FST_ERR_CTP_EMPTY_JSON_BODY — an empty body is not valid JSON, and several
  // endpoints here take no body at all (extract, withdraw, cancel, logout).
  const headers: HeadersInit = init.body === undefined
    ? { ...init.headers }
    : { 'Content-Type': 'application/json', ...init.headers };

  const res = await fetch(path, { ...init, credentials: 'same-origin', headers });

  if (res.status === 204) return undefined as T;

  let body: unknown;
  try { body = await res.json(); } catch { body = null; }

  if (!res.ok) {
    const message =
      (body as { error?: string } | null)?.error ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message);
  }
  return body as T;
}

export const api = {
  get:  <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
};
